import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/Card';
import { KpiCard } from '../components/KpiCard';
import { Pill } from '../components/Pill';
import { Modal } from '../components/Modal';
import { CopyField } from '../components/CopyField';
import { EarningsChart } from '../components/EarningsChart';
import { Icon } from '../components/Icons';
import { btnGhost, btnPrimary, tableStyle, tdStyle, thStyle } from '../components/buttons';
import { adminApi } from '../api/admin';
import { fmtDate, fmtNum, fmtUSD } from '../lib/format';
import type { ApiError } from '../api/client';
import type { CreatorWithStatsDto } from '../api/types';

export function CreatorDetailScreen() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [resetPwResult, setResetPwResult] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const creator = useQuery({ queryKey: ['admin', 'creator', id], queryFn: () => adminApi.creator(id), enabled: !!id });
  const codes = useQuery({ queryKey: ['admin', 'creator-codes', id], queryFn: () => adminApi.creatorCodes(id), enabled: !!id });
  const series = useQuery({
    queryKey: ['admin', 'creator-series', id],
    queryFn: () => adminApi.creatorEarningsSeries(id, '12M'),
    enabled: !!id,
  });
  const payouts = useQuery({
    queryKey: ['admin', 'creator-payouts', id],
    queryFn: () => adminApi.creatorPayouts(id),
    enabled: !!id,
  });
  const activity = useQuery({
    queryKey: ['admin', 'creator-activity', id],
    queryFn: () => adminApi.creatorActivity(id, 15),
    enabled: !!id,
  });

  const suspend = useMutation({
    mutationFn: () => adminApi.suspendCreator(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin'] }),
    onError: (err: ApiError) => setActionError(err?.message ?? 'Failed'),
  });
  const reactivate = useMutation({
    mutationFn: () => adminApi.reactivateCreator(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin'] }),
    onError: (err: ApiError) => setActionError(err?.message ?? 'Failed'),
  });
  const resetPw = useMutation({
    mutationFn: () => adminApi.resetCreatorPassword(id),
    onSuccess: (res) => setResetPwResult(res.tempPassword),
    onError: (err: ApiError) => setActionError(err?.message ?? 'Failed'),
  });

  const c = creator.data;

  return (
    <div style={{ padding: '24px 28px 60px', display: 'grid', gap: 18, maxWidth: 1280 }}>
      <button
        onClick={() => navigate('/creators')}
        style={{ alignSelf: 'flex-start', fontSize: 12, color: 'var(--ink-3)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        ← Back to creators
      </button>

      {/* Profile + actions */}
      <Card padding={24}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'var(--brand)',
              color: 'var(--bg-elev)',
              display: 'grid',
              placeItems: 'center',
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 20,
              flexShrink: 0,
            }}
          >
            {c?.avatarInitials ?? '–'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>
                {c?.name ?? '—'}
              </div>
              {c?.status === 'active' ? <Pill tone="good">Active</Pill> : c?.status === 'suspended' ? <Pill tone="bad">Suspended</Pill> : null}
              {c && <Pill tone="brand">{(c.commissionRate * 100).toFixed(0)}% commission</Pill>}
              {c?.mustChangePassword && <Pill tone="warn">Password pending</Pill>}
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
              @{c?.handle ?? '—'} · {c?.email ?? '—'} · joined {c ? fmtDate(c.joinedAt) : '—'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={btnGhost} onClick={() => setResetPwOpen(true)} disabled={resetPw.isPending}>
              Reset password
            </button>
            {c?.status === 'active' ? (
              <button style={btnGhost} onClick={() => suspend.mutate()} disabled={suspend.isPending}>
                Suspend
              </button>
            ) : c?.status === 'suspended' ? (
              <button style={btnPrimary} onClick={() => reactivate.mutate()} disabled={reactivate.isPending}>
                Reactivate
              </button>
            ) : null}
          </div>
        </div>
        {actionError && (
          <div
            style={{
              marginTop: 12,
              fontSize: 12.5,
              color: 'var(--bad)',
              background: 'color-mix(in oklab, var(--bad) 10%, var(--bg-elev))',
              border: '1px solid color-mix(in oklab, var(--bad) 25%, transparent)',
              padding: '8px 12px',
              borderRadius: 'var(--r-sm)',
            }}
          >
            {actionError}
          </div>
        )}
      </Card>

      {/* Creator-branded paywall */}
      {c && <PaywallCard id={id} creator={c} />}

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        <KpiCard label="Lifetime commission" value={c?.stats?.lifetimeCommissionCents ?? 0} format={fmtUSD} decimals={2} accent />
        <KpiCard label="Active subs" value={c?.stats?.activeSubs ?? 0} />
        <KpiCard label="Total subs attributed" value={c?.stats?.totalSubs ?? 0} />
        <KpiCard label="Codes (approved)" value={(codes.data ?? []).filter((k) => k.approval === 'approved').length} sublabel={`of ${(codes.data ?? []).length} total`} />
      </div>

      {/* Chart + activity */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)', gap: 14 }} className="snapmon-chart-row">
        <Card padding={20} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Monthly commission · last 12 months</div>
            <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 2 }}>Creator's 10% cut per period</div>
          </div>
          {series.data && series.data.length > 0 ? (
            <EarningsChart data={series.data.map((p) => ({ label: p.label, amountCents: p.amountCents }))} height={240} />
          ) : (
            <div style={{ height: 240, display: 'grid', placeItems: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
              {series.isLoading ? 'Loading…' : 'No earnings yet'}
            </div>
          )}
        </Card>

        <Card padding={0} style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '18px 20px 8px', borderBottom: '1px solid var(--line)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Recent activity</div>
            <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 2 }}>Latest events for this creator</div>
          </div>
          {(activity.data ?? []).length === 0 ? (
            <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 12 }}>
              {activity.isLoading ? 'Loading…' : 'No activity yet.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {(activity.data ?? []).map((a, i) => (
                <div
                  key={a.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 20px',
                    borderBottom: i === (activity.data ?? []).length - 1 ? 'none' : '1px solid var(--line)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {a.eventType.replace('_', ' ')}{' '}
                      <span style={{ color: 'var(--ink-3)' }}>
                        · {a.plan ?? '–'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', display: 'flex', gap: 8, marginTop: 2 }}>
                      {a.code && <span className="mono">{a.code}</span>}
                      {a.trainerId && <span className="mono">{a.trainerId}</span>}
                      <span>{new Date(a.occurredAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                    </div>
                  </div>
                  {a.amountCents != null && (
                    <div className="num" style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)' }}>
                      +{fmtUSD(a.amountCents)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Codes table */}
      <Card padding={0}>
        <div style={{ padding: '18px 20px' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Codes</div>
          <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 2 }}>All affiliate codes owned by this creator</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Uses</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Active</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>MRR</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Lifetime</th>
              </tr>
            </thead>
            <tbody>
              {(codes.data ?? []).map((k) => (
                <tr key={k.id} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={tdStyle}>
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontWeight: 600,
                        fontSize: 13,
                        background: 'var(--brand-soft)',
                        color: 'var(--brand-ink)',
                        padding: '3px 8px',
                        borderRadius: 'var(--r-sm)',
                      }}
                    >
                      {k.code}
                    </span>
                    <span style={{ marginLeft: 8, color: 'var(--ink-3)', fontSize: 12 }}>{k.label}</span>
                  </td>
                  <td style={tdStyle}>{k.kind === 'primary' ? <Pill tone="brand" size="sm">◆ Primary</Pill> : <Pill tone="neutral" size="sm">Side</Pill>}</td>
                  <td style={tdStyle}>
                    {k.approval === 'approved' && <Pill tone="good" size="sm">Approved</Pill>}
                    {k.approval === 'pending' && <Pill tone="warn" size="sm">Pending</Pill>}
                    {k.approval === 'rejected' && <Pill tone="bad" size="sm">Rejected</Pill>}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }} className="num">{fmtNum(k.uses ?? 0)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }} className="num">{fmtNum(k.activeSubs ?? 0)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }} className="num">{fmtUSD(k.mrrCents ?? 0)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }} className="num">{fmtUSD(k.lifetimeCents ?? 0)}</td>
                </tr>
              ))}
              {(codes.data ?? []).length === 0 && (
                <tr><td colSpan={7} style={{ ...tdStyle, color: 'var(--ink-3)', textAlign: 'center', padding: 30 }}>No codes yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Payouts table */}
      <Card padding={0}>
        <div style={{ padding: '18px 20px' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Payouts</div>
          <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 2 }}>Monthly close-out history for this creator</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Payout ID</th>
                <th style={thStyle}>Period</th>
                <th style={thStyle}>Method</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {(payouts.data ?? []).map((p) => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={tdStyle}><span className="mono" style={{ fontWeight: 600 }}>{p.publicId}</span></td>
                  <td style={{ ...tdStyle, color: 'var(--ink-2)' }}>{fmtDate(p.periodMonth)}</td>
                  <td style={{ ...tdStyle, color: 'var(--ink-2)' }}>{p.methodLabel}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }} className="num">{fmtUSD(p.amountCents)}</td>
                  <td style={tdStyle}><PayoutStatusPill status={p.status} /></td>
                </tr>
              ))}
              {(payouts.data ?? []).length === 0 && (
                <tr><td colSpan={5} style={{ ...tdStyle, color: 'var(--ink-3)', textAlign: 'center', padding: 30 }}>No payouts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        open={resetPwOpen}
        onClose={() => {
          setResetPwOpen(false);
          setResetPwResult(null);
        }}
        title={resetPwResult ? 'Password reset · new temp issued' : 'Reset password?'}
        description={
          resetPwResult
            ? 'Copy this once. The creator must change it on their next login.'
            : 'Generates a new one-time temp password and force-flips must_change_password=1.'
        }
        footer={
          resetPwResult ? (
            <button style={btnPrimary} onClick={() => { setResetPwOpen(false); setResetPwResult(null); }}>
              Done
            </button>
          ) : (
            <>
              <button style={btnGhost} onClick={() => setResetPwOpen(false)}>Cancel</button>
              <button style={btnPrimary} onClick={() => resetPw.mutate()} disabled={resetPw.isPending}>
                {resetPw.isPending ? 'Resetting…' : 'Reset password'}
              </button>
            </>
          )
        }
      >
        {resetPwResult ? <CopyField value={resetPwResult} label="Temp pw" big /> : <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>This will invalidate the creator's current password.</div>}
      </Modal>
    </div>
  );
}

function PaywallCard({ id, creator }: { id: number; creator: CreatorWithStatsDto }) {
  const qc = useQueryClient();
  const [displayName, setDisplayName] = useState(creator.paywallDisplayName ?? '');
  const [newImage, setNewImage] = useState<string | null>(null); // data-URL of a freshly picked file
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const previewSrc = newImage ?? creator.paywallImageUrl;
  const bothSet = !!previewSrc && !!displayName.trim();

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'creator', id] });

  const save = useMutation({
    mutationFn: () =>
      adminApi.updateCreator(id, {
        paywallDisplayName: displayName.trim() ? displayName.trim() : null,
        ...(newImage ? { paywallImageBase64: newImage } : {}),
      }),
    onSuccess: () => {
      setNewImage(null);
      setSaved(true);
      invalidate();
    },
    onError: (err: ApiError) => setError(err?.message ?? 'Save failed'),
  });

  const removeImage = useMutation({
    mutationFn: () => adminApi.updateCreator(id, { clearPaywallImage: true }),
    onSuccess: invalidate,
    onError: (err: ApiError) => setError(err?.message ?? 'Failed'),
  });

  const onPickFile = (file: File | undefined) => {
    setError(null);
    setSaved(false);
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      setError('Image must be PNG, JPEG, or WEBP');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setError('Image too large (max 4MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setNewImage(reader.result as string);
    reader.readAsDataURL(file);
  };

  return (
    <Card padding={24} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Creator paywall</div>
        <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 2 }}>
          Set <b>both</b> a photo and a display name to enable the branded paywall (avatar + name) for users with
          this creator's code. If either is missing, they still get the 10% discounted paywall.
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            overflow: 'hidden',
            flexShrink: 0,
            background: 'var(--bg-elev)',
            border: '1px solid var(--line)',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--ink-3)',
            fontSize: 11,
            textAlign: 'center',
          }}
        >
          {previewSrc ? (
            <img src={previewSrc} alt="Paywall" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            'No photo'
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minWidth: 240 }}>
          <label style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            Display name
            <input
              value={displayName}
              maxLength={100}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setSaved(false);
              }}
              placeholder="e.g. MR.BEAST"
              style={{
                width: '100%',
                marginTop: 4,
                padding: '10px 12px',
                borderRadius: 'var(--r-sm)',
                border: '1px solid var(--line)',
                background: 'var(--bg-elev)',
                color: 'var(--ink-1)',
                fontSize: 14,
                fontFamily: 'var(--font-display)',
              }}
            />
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ ...btnGhost, cursor: 'pointer' }}>
              {previewSrc ? 'Replace photo' : 'Upload photo'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                onChange={(e) => onPickFile(e.target.files?.[0])}
              />
            </label>
            {creator.paywallImageUrl && !newImage && (
              <button style={btnGhost} onClick={() => removeImage.mutate()} disabled={removeImage.isPending}>
                {removeImage.isPending ? 'Removing…' : 'Remove photo'}
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div
          style={{
            fontSize: 12.5,
            color: 'var(--bad)',
            background: 'color-mix(in oklab, var(--bad) 10%, var(--bg-elev))',
            border: '1px solid color-mix(in oklab, var(--bad) 25%, transparent)',
            padding: '8px 12px',
            borderRadius: 'var(--r-sm)',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button style={btnPrimary} onClick={() => { setError(null); save.mutate(); }} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save paywall'}
        </button>
        {saved && <span style={{ fontSize: 12.5, color: 'var(--good)' }}>Saved.</span>}
        <span style={{ fontSize: 12, color: bothSet ? 'var(--good)' : 'var(--ink-3)' }}>
          {bothSet ? '✓ Branded paywall active' : 'Branded paywall needs both photo + name'}
        </span>
      </div>
    </Card>
  );
}

function PayoutStatusPill({ status }: { status: string }) {
  if (status === 'paid') return <Pill tone="good" size="sm">Paid</Pill>;
  if (status === 'scheduled') return <Pill tone="warn" size="sm">Scheduled</Pill>;
  if (status === 'processing') return <Pill tone="warn" size="sm">Processing</Pill>;
  if (status === 'failed') return <Pill tone="bad" size="sm">Failed</Pill>;
  if (status === 'skipped') return <Pill tone="neutral" size="sm">Skipped</Pill>;
  return <Pill tone="neutral" size="sm">{status}</Pill>;
}
